/**
 * Pure helpers for merging T1 run funnel counts with definition stages.
 */

import type { T1FunnelSnapshot, T1PatientEvaluation } from "../../api/clinicalTrialsApiTypes";

export type T1FunnelStageId =
  | "candidate"
  | "t1_passed"
  | "t1_dropped"
  | "t1_unresolved"
  | "t1_error";

/** @deprecated Prefer T1FunnelStageId — kept for older imports. */
export type T1FunnelStageKey = T1FunnelStageId;

export type T1FunnelStage = {
  stageId: T1FunnelStageId;
  label: string;
  count: number;
  description: string;
};

/**
 * Build truthful Live T1 funnel stages from a persisted snapshot.
 *
 * @param funnel - Materialized funnel snapshot
 * @returns Ordered stage cards for the Live T1 view
 */
export function buildT1FunnelStages(funnel: T1FunnelSnapshot): T1FunnelStage[] {
  return [
    {
      stageId: "candidate",
      label: "Candidates",
      count: funnel.candidate_count,
      description: "Patients in the cohort snapshot",
    },
    {
      stageId: "t1_passed",
      label: "T1 passed",
      count: funnel.t1_passed_count,
      description: "Applicable T1 checks passed — not final eligibility",
    },
    {
      stageId: "t1_dropped",
      label: "T1 dropped",
      count: funnel.t1_dropped_count,
      description: "Hard-stop failure ended T1 for these patients",
    },
    {
      stageId: "t1_unresolved",
      label: "T1 unresolved",
      count: funnel.t1_unresolved_count,
      description: "Missing or unknown structured evidence",
    },
    {
      stageId: "t1_error",
      label: "T1 errors",
      count: funnel.t1_error_count,
      description: "Infrastructure failures during evaluation",
    },
  ];
}

/**
 * Map a funnel stage id to the API disposition filter when applicable.
 *
 * @param stageId - Funnel stage identifier
 * @returns Disposition filter or null for the full candidate list
 */
export function dispositionForStage(stageId: T1FunnelStageId): string | null {
  if (stageId === "t1_passed") return "T1_PASSED";
  if (stageId === "t1_dropped") return "T1_DROPPED";
  if (stageId === "t1_unresolved") return "T1_UNRESOLVED";
  if (stageId === "t1_error") return "T1_ERROR";
  return null;
}

/**
 * Verify funnel accounting identity for UI badges.
 *
 * @param funnel - Materialized funnel snapshot
 * @returns True when candidate equals the sum of terminal buckets
 */
export function funnelReconciles(funnel: T1FunnelSnapshot): boolean {
  return (
    funnel.candidate_count ===
    funnel.t1_passed_count +
      funnel.t1_dropped_count +
      funnel.t1_unresolved_count +
      funnel.t1_error_count
  );
}

/**
 * Summarize patient rows for compact list rendering.
 *
 * @param patients - Patient evaluation rows
 * @returns Short display labels
 */
export function summarizePatients(patients: T1PatientEvaluation[]): string[] {
  return patients.map((patient) => {
    const dropout = patient.first_dropout_node_id
      ? ` · dropout ${patient.first_dropout_node_id}`
      : "";
    return `${patient.mrn} · ${patient.disposition}${dropout}`;
  });
}
